import { Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { sanitizeEmail } from "@entrilia/shared/utilities/emailHelper";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import { emailValidator } from "@entrilia/shared/utilities/validators";
import type { ClientInfo } from "../../../api/clientUsersTypes";
import { api } from "../../../api/operationsApi";
import { useExecutionState } from "../../../hooks/useExecutionState";
import ExecutionStatusAlert from "../../common/ExecutionStatusAlert";
import FieldHelp from "../../common/FieldHelp";
import LoadingFailedAlert from "../../common/LoadingFailedAlert";
import PageTitle from "../../common/PageTitle";

const resetUserMfa = withErrorHandling(api.clientUsers.resetUserMfa);

const ResetMfaContent = () => {
  const { executionState, setExecutionState } = useExecutionState();
  const [selectedOrganization, setSelectedOrganization] = useState<ClientInfo | null>(null);
  const [userEmail, setUserEmail] = useState("");
  const [emailValidationResult, setEmailValidationResult] = useState<ValidationResult>();

  const [organizationsResponse, fetchOrganizationsError] = useFetch(api.clientUsers.getOrganizations);

  if (fetchOrganizationsError) {
    return <LoadingFailedAlert apiError={fetchOrganizationsError} />;
  }

  if (organizationsResponse === undefined) {
    return <InlineLoader />;
  }

  const organizations = organizationsResponse.organizations;

  const handleResetMfa = async () => {
    if (!selectedOrganization || !userEmail.trim()) {
      return;
    }

    const sanitizedEmail = sanitizeEmail(userEmail);
    const validationResult = emailValidator(sanitizedEmail);
    setEmailValidationResult(validationResult);
    if (!validationResult.isValid) {
      return;
    }

    setExecutionState({ status: "in-progress" });

    const [resp, error] = await resetUserMfa({
      clientCode: selectedOrganization.clientCode,
      email: sanitizedEmail,
    });

    if (error) {
      setExecutionState({ status: "error", error });
      return;
    }

    const bothSucceeded = resp.clientTenantResetSuccess && resp.entriliaTenantResetSuccess;

    if (!resp.clientTenantResetSuccess) {
      setExecutionState({
        status: "error",
        text: "Failed",
        errorMessage: `Failed to reset MFA for user ${resp.email} in client tenant.`,
      });
      return;
    }

    if (bothSucceeded) {
      setExecutionState({
        status: "success",
        text: "MFA Reset Successful",
        details: [
          `MFA settings have been successfully reset for user ${resp.email} in both ${selectedOrganization.name} and Entrilia tenants.`,
          "The user will need to re-register their MFA methods on next sign-in.",
        ],
      });
      return;
    }

    setExecutionState({
      status: "error",
      text: "Partially Successful",
      errorMessage:
        `MFA settings have been reset for user ${resp.email} in ${selectedOrganization.name} tenant.\n\n` +
        "⚠️ However, MFA reset in Entrilia tenant failed due to an error. This may indicate a connectivity issue or permissions problem.\n\n" +
        "The user will need to re-register their MFA methods on next sign-in to the client tenant.",
    });
  };

  return (
    <Stack spacing={3} sx={{ alignItems: "flex-start", width: "24rem" }}>
      <PageTitle title="Reset User MFA" subtitle="🔐 What this does" defaultExpanded>
        Reset MFA authentication methods for users in both organization Azure AD tenant (BC AAD) and Entrilia tenant.
        <br />
        <br />
        • Removes all MFA authentication methods (phone, authenticator app, etc.) from both tenants
        <br />
        • Revokes all active sign-in sessions
        <br />
        • Forces the user to re-register MFA on next sign-in
        <br />
        <br />
        ⚠️ <strong>Warning:</strong> This action will immediately sign out the user from all devices and require them to
        set up MFA again.
      </PageTitle>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <FormControl fullWidth>
          <InputLabel>Organization</InputLabel>
          <Select
            label="Organization"
            value={selectedOrganization?.clientCode || ""}
            onChange={(e) => {
              const org = organizations.find((o) => o.clientCode === e.target.value);
              setSelectedOrganization(org || null);
            }}
          >
            {organizations.map((org) => (
              <MenuItem key={org.clientCode} value={org.clientCode}>
                {org.name} ({org.clientCode})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FieldHelp>
          Select the organization whose Azure AD tenant contains the user. This determines which Active Directory the
          MFA reset will be performed in.
        </FieldHelp>
      </Stack>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <TextField
          fullWidth
          label="User Email"
          value={userEmail}
          onChange={(e) => setUserEmail(e.target.value)}
          error={emailValidationResult?.isValid === false}
          helperText={emailValidationResult?.error}
        />
        <FieldHelp>
          Email address of the user whose MFA settings need to be reset. This must be an email address from the selected
          organization&apos;s Azure Active Directory.
        </FieldHelp>
      </Stack>
      <Button
        variant="contained"
        color="primary"
        onClick={handleResetMfa}
        loading={executionState.status === "in-progress"}
        disabled={!selectedOrganization || !userEmail.trim()}
      >
        Reset MFA
      </Button>
      <ExecutionStatusAlert executionState={executionState} setExecutionState={setExecutionState} />
    </Stack>
  );
};

export default ResetMfaContent;
